// Shared scenario / operative-state definitions for the interactive HighTension
// training (SRS). Kept framework-free so the backend scoring can mirror the same
// weight table. State semantics come from regulament.txt (operative states):
//  - schema normala (in service): bar sep on normal bar closed, line sep closed,
//    breaker closed, CLP open
//  - deconectat / rezerva calda / stare calda (§706/§629): separators closed,
//    breaker open, CLP open
//  - separat vizibil / rezerva rece / stare rece (§726/§637/§647): all separators
//    open, breaker open, CLP open
//  - legat la pamant (§772): all separators open, breaker open, all CLPs closed

// The four distinct switch-position classes a target/start state collapses to.
export type StateClass = "NORMAL" | "DISCONNECTED" | "VISIBLY_SEPARATED" | "EARTHED" | "ALL_OPEN";

// Required switch positions for a state class. "closed"/"open" for the bay's
// switches; barSep applies to every bar separator of the bay.
export interface StateRule {
    barSep: "closed" | "open";
    lineSep: "closed" | "open";
    breaker: "closed" | "open";
    clp: "closed" | "open";
    // NORMAL start energizes the bay (generator started / bars live) so the
    // current-based interlocks (CLP under voltage, parallel) behave realistically.
    energize: boolean;
}

export const STATE_RULE: Record<StateClass, StateRule> = {
    NORMAL:             { barSep: "closed", lineSep: "closed", breaker: "closed", clp: "open",   energize: true },
    DISCONNECTED:       { barSep: "closed", lineSep: "closed", breaker: "open",   clp: "open",   energize: true },
    VISIBLY_SEPARATED:  { barSep: "open",   lineSep: "open",   breaker: "open",   clp: "open",   energize: false },
    EARTHED:            { barSep: "open",   lineSep: "open",   breaker: "open",   clp: "closed", energize: false },
    // Free roam: nothing preset — the scheme stays as built (all switches open).
    ALL_OPEN:           { barSep: "open",   lineSep: "open",   breaker: "open",   clp: "open",   energize: false },
};

export interface ScenarioDef {
    id: string;
    roLabel: string;
    startState: StateClass;
    // null = free roam (no target, no scoring).
    targetState: StateClass | null;
}

// Scenario ids reuse the strings already used by the Invatare module
// (HighTensionProjects.tsx). Most scenarios take the bay from in-service
// (schema normala) to the requested state; "revenire" goes the other way.
export const SCENARIOS: Record<string, ScenarioDef> = {
    free:           { id: "free",           roLabel: "Liber (free roam)",          startState: "NORMAL",            targetState: null },
    "closing-cell": { id: "closing-cell",   roLabel: "Revenire la schema normală", startState: "EARTHED",           targetState: "NORMAL" },
    disconnected:   { id: "disconnected",   roLabel: "Deconectată",                startState: "NORMAL",            targetState: "DISCONNECTED" },
    "warm-condition":{ id: "warm-condition",roLabel: "Stare caldă",                startState: "NORMAL",            targetState: "DISCONNECTED" },
    "warm-reserve": { id: "warm-reserve",   roLabel: "Rezervă caldă",              startState: "NORMAL",            targetState: "DISCONNECTED" },
    ssv:            { id: "ssv",            roLabel: "Stare separat vizibilă",     startState: "NORMAL",            targetState: "VISIBLY_SEPARATED" },
    "cold-condition":{ id: "cold-condition",roLabel: "Stare rece",                 startState: "NORMAL",            targetState: "VISIBLY_SEPARATED" },
    "cold-reserve": { id: "cold-reserve",   roLabel: "Rezervă rece",               startState: "NORMAL",            targetState: "VISIBLY_SEPARATED" },
    slp:            { id: "slp",            roLabel: "Stare legată la pământ",     startState: "NORMAL",            targetState: "EARTHED" },
};

// Human labels for each state class (RO).
export const STATE_LABEL: Record<StateClass, string> = {
    NORMAL: "Schemă normală (în funcțiune)",
    DISCONNECTED: "Deconectat",
    VISIBLY_SEPARATED: "Separat vizibil",
    EARTHED: "Legat la pământ",
    ALL_OPEN: "Toate deschise",
};

// Short RO description shown on the create form for each scenario.
export const SCENARIO_DESC: Record<string, string> = {
    free: "Exersezi liber, fără stare țintă și fără punctaj. Stația pornește în starea normală de funcționare.",
    "closing-cell": "Revenire la schema normală: pornind din starea legată la pământ, deschizi CLP-urile, apoi închizi separatoarele de bară, separatorul de linie și întreruptorul.",
    disconnected: "Scoți celula din funcțiune: deconectezi întreruptorul; separatoarele rămân închise.",
    "warm-condition": "Stare caldă (echipament indisponibil): aceleași poziții ca la 'deconectat'.",
    "warm-reserve": "Rezervă caldă: întreruptor deconectat, separatoare închise — gata de anclanșare.",
    ssv: "Separat vizibil: deschizi întreruptorul și toate separatoarele.",
    "cold-condition": "Stare rece (indisponibil): separatoare deschise, întreruptor deconectat.",
    "cold-reserve": "Rezervă rece: separatoare deschise spre bară și spre echipament, întreruptor deconectat.",
    slp: "Legat la pământ: pornind din schema normală, deschizi întreruptorul, separatorul de linie și separatoarele de bară, apoi închizi CLP-urile.",
};

// The switch changes between two states (start -> target), for the transition
// visual. Only aspects that differ are returned.
export interface TransitionStep { key: string; label: string; from: string; to: string; }

// Generator runs only in normal operation (energized + breaker closed).
export const genOnForState = (state: StateClass): boolean => {
    const r = STATE_RULE[state];
    return r.energize && r.breaker === "closed";
};

// Safe operating-maneuver order. A disconnector (separator) is never switched
// under load, so:
//  - De-energizing (opening): STOP the generator first, then open the breaker,
//    line separator, bar separators; earthing (CLP close) happens last.
//  - Energizing (closing): remove earthing (CLP open) first, START the generator,
//    then close bar separator, line separator, and the breaker last.
// Lower weight = executed earlier.
const STEP_WEIGHT: Record<string, number> = {
    "gen:off": -1,     // stop the generator before anything else
    "clp:open": 0,
    "gen:on": 0.5,     // start the generator right after the CLP is opened
    "breaker:open": 1,
    "lineSep:open": 2,
    "barSep:open": 3,
    "barSep:closed": 4,
    "lineSep:closed": 5,
    "breaker:closed": 6,
    "clp:closed": 7,
};

export function transitionSteps(start: StateClass, target: StateClass, includeGenerator = false): TransitionStep[] {
    const a = STATE_RULE[start], b = STATE_RULE[target];
    const rows: { key: keyof StateRule & string, label: string }[] = [
        { key: "barSep", label: "Separatoare de bară" },
        { key: "lineSep", label: "Separator de linie" },
        { key: "breaker", label: "Întreruptor" },
        { key: "clp", label: "CLP (legare la pământ)" },
    ];
    const out: (TransitionStep & { weight: number })[] = [];
    rows.forEach((r) => {
        const from = (a as any)[r.key] as "closed" | "open";
        const to = (b as any)[r.key] as "closed" | "open";
        if (from !== to) out.push({
            key: r.key,
            label: r.label,
            from: from === "closed" ? "închis" : "deschis",
            to: to === "closed" ? "închis" : "deschis",
            weight: STEP_WEIGHT[`${r.key}:${to}`] ?? 99,
        });
    });
    // Generator start/stop — only for scopes that actually contain a generator.
    const genA = genOnForState(start), genB = genOnForState(target);
    if (includeGenerator && genA !== genB) out.push({
        key: "gen", label: "Generator",
        from: genA ? "pornit" : "oprit", to: genB ? "pornit" : "oprit",
        weight: STEP_WEIGHT[genB ? "gen:on" : "gen:off"],
    });
    out.sort((x, y) => x.weight - y.weight);
    return out.map(({ weight, ...s }) => s);
}

export const SCENARIO_LIST: ScenarioDef[] = [
    SCENARIOS["free"],
    SCENARIOS["closing-cell"],
    SCENARIOS["disconnected"],
    SCENARIOS["warm-condition"],
    SCENARIOS["warm-reserve"],
    SCENARIOS["ssv"],
    SCENARIOS["cold-condition"],
    SCENARIOS["cold-reserve"],
    SCENARIOS["slp"],
];

// Selectable schemes. Only SRS is implemented for now.
export interface SchemeOpt { id: string; label: string; enabled: boolean; }
export const SCHEMES: SchemeOpt[] = [
    { id: "SRS",      label: "SRS",      enabled: true },
    { id: "SRA",      label: "SRA",      enabled: false },
];

// Busbar sections a cell taps — used for semistation grouping + whole-station apply.
export type Section = "IA" | "IB" | "IIA" | "IIB";

// normalBar: the busbar this cell takes voltage from in normal operation (the
//   closed bar separator in the station's normal state — see the reference photo).
// isMeasurement: a C.M. (celula de masura) cell whose CLP earths its whole busbar.
// NOTE: the normalBar values are a best-effort reading of the normal-state photo
// and are meant to be corrected here if any bay sits on the other bus.
// normalState: the operative state this cell sits in during the station's normal
//   operation (default NORMAL). A few cells are normally NOT in service — e.g. the
//   Donalam reserve line (disconnected) and the C.T.V cells (visibly separated).
// isCoupler: a transversal coupling (CTV) that bridges bar I and bar II of a
//   semistation — in its "connected/normal" state BOTH bar separators + the
//   breaker are closed (not just the normal bar like an ordinary bay).
// isLongitudinal: a longitudinal coupling (C.L.) that ties the two semistations
//   on the same bus system. It is a selectable/operable cell, and it is part of a
//   SEMISTATION scope (the semistation is only really in the requested state once
//   the couplings toward the other semistation are there too), but it stays out
//   of line / whole-station scopes.
export interface CellDef { id: string; label: string; sections: Section[]; normalBar: Section; isMeasurement?: boolean; normalState?: StateClass; isCoupler?: boolean; isLongitudinal?: boolean; }

// Cell ids match the bay `name` passed into the layout builders in
// HighTensionTraining.tsx (topBay/bottomBay/cmTop/cmBottom).
export const SRS_CELLS: CellDef[] = [
    { id: "OE4",                  label: "LEA SRA OE4 (CEL 28K)",            sections: ["IB", "IIB"], normalBar: "IB" },
    { id: "LEA Donalam",                label: "LEA Donalam (CEL 26K)",          sections: ["IB", "IIB"], normalBar: "IB" },
    { id: "OE2",                  label: "LEA SRA OE2 (CEL 24K)",            sections: ["IB", "IIB"], normalBar: "IIB" },
    { id: "C.T.V - B",            label: "C.T.V - B (CEL 22K)",      sections: ["IB", "IIB"], normalBar: "IB", normalState: "VISIBLY_SEPARATED", isCoupler: true },
    { id: "GENERATOR 1",          label: "GENERATOR 1 (CEL 20K)",    sections: ["IB", "IIB"], normalBar: "IIB" },
    { id: "C.M. IB",              label: "C.M. IB (CEL 30K)",        sections: ["IB"],        normalBar: "IB",  isMeasurement: true },
    { id: "C.M. II B",            label: "C.M. II B (CEL 30K)",      sections: ["IIB"],       normalBar: "IIB", isMeasurement: true },
    { id: "Trafo 4T",             label: "Trafo 4T (CEL 27K)",       sections: ["IB", "IIB"], normalBar: "IB" },
    { id: "TSI-2",             label: "TSI-2 (CEL 21K)",       sections: ["IB", "IIB"], normalBar: "IB" },
    { id: "LEA Rezerva Donalam",  label: "LEA Rezerva Donalam (CEL 23K)", sections: ["IB"],   normalBar: "IB", normalState: "VISIBLY_SEPARATED" },
    { id: "C.M. I A",             label: "C.M. I A (CEL 2K)",        sections: ["IA"],        normalBar: "IA",  isMeasurement: true },
    { id: "TSI-1",             label: "TSI-1 (CEL 9K)",        sections: ["IA", "IIA"], normalBar: "IA" },
    { id: "Trafo T1",             label: "Trafo T1 (CEL 3K)",        sections: ["IA", "IIA"], normalBar: "IA" },
    { id: "C.M. II A",            label: "C.M. II A (CEL 2K)",       sections: ["IIA"],       normalBar: "IIA", isMeasurement: true },
    { id: "GENERATOR 2",          label: "GENERATOR 2 (CEL 12K)",    sections: ["IA", "IIA"], normalBar: "IA" },
    { id: "C.T.V - A",            label: "C.T.V - A (CEL 10K)",      sections: ["IA", "IIA"], normalBar: "IA", normalState: "VISIBLY_SEPARATED", isCoupler: true },
    { id: "OE3",                  label: "LEA SRA OE3 (CEL 8K)",             sections: ["IA", "IIA"], normalBar: "IA" },
    { id: "OE1",                  label: "LEA SRA OE1 (CEL 4K)",             sections: ["IA", "IIA"], normalBar: "IIA" },
    { id: "C.L. II B - II A",     label: "C.L. II B - II A (CEL 13K - 17K)", sections: ["IIB", "IIA"], normalBar: "IIB", normalState: "NORMAL", isCoupler: true, isLongitudinal: true },
    { id: "C.L. I B - I A",       label: "C.L. I B - I A (CEL 14K - 18K)",   sections: ["IB", "IA"],   normalBar: "IB",  normalState: "NORMAL", isCoupler: true, isLongitudinal: true },
];

// Working scopes offered on the create form: whole station, a semistation (A/B),
// or a single busbar line (IA/IIA/IB/IIB). Single cells are added in the form.
export const LINES: { id: Section; label: string }[] = [
    { id: "IA",  label: "S.B - IA" },
    { id: "IIA", label: "S.B - IIA" },
    { id: "IB",  label: "S.B - IB" },
    { id: "IIB", label: "S.B - IIB" },
];
export const SEMISTATIONS: { id: string; label: string }[] = [
    { id: "A", label: "Semistația A" },
    { id: "B", label: "Semistația B" },
];
export const WHOLE_STATION = "*";

const isLine = (s: string): s is Section => (LINES as { id: string }[]).some((l) => l.id === s);
const isSemistation = (s: string) => s === "A" || s === "B";

// RO name of a scope selection, for banners / reports: a bare "A" reads as
// "Statia A", not as a cell id.
export function scopeLabel(selection: string): string {
    if (selection === WHOLE_STATION) return "Toată stația";
    if (isSemistation(selection)) return `Stația ${selection}`;
    if (isLine(selection)) return LINES.find((l) => l.id === selection)?.label || selection;
    return SRS_CELLS.find((c) => c.id === selection)?.label || selection;
}
export const isSingleCellScope = (selection: string) =>
    selection !== WHOLE_STATION && !isLine(selection) && !isSemistation(selection);

// The longitudinal couplings (C.L.) that touch a semistation: both of them, since
// C.L. I B - I A ties bar I and C.L. II B - II A ties bar II of the two halves.
const longitudinalsOf = (semi: "A" | "B"): string[] =>
    SRS_CELLS.filter((c) => c.isLongitudinal && c.sections.some((s) => s.endsWith(semi))).map((c) => c.id);

// Resolve a scope selection to the concrete bay cell ids it covers.
//  - "*"              -> whole station
//  - "A" / "B"        -> that semistation (both its busbars) + its longitudinal couplings
//  - "IA"/"IIA"/...   -> one busbar line: the cells sitting on it in normal state
//  - <cell id>        -> that single cell
export function resolveCells(selection: string): string[] {
    // Longitudinal couplings stay closed during line / whole-station work, so they
    // are excluded from those scopes. A semistation, on the other hand, can only
    // reach the requested state if the couplings toward the other semistation are
    // brought there as well — otherwise the other half keeps feeding it.
    const multi = SRS_CELLS.filter((c) => !c.isLongitudinal);
    if (selection === WHOLE_STATION) return multi.map((c) => c.id);
    if (selection === "A") return [...multi.filter((c) => c.normalBar === "IA" || c.normalBar === "IIA").map((c) => c.id), ...longitudinalsOf("A")];
    if (selection === "B") return [...multi.filter((c) => c.normalBar === "IB" || c.normalBar === "IIB").map((c) => c.id), ...longitudinalsOf("B")];
    if (isLine(selection)) return multi.filter((c) => c.normalBar === selection).map((c) => c.id);
    return [selection];
}

// ---- Per-cell equipment + scenario applicability ------------------------------
// A cell can only reach a state it has the switchgear for:
//  - DISCONNECTED needs a breaker  -> measurement cells (no breaker) can't.
//  - EARTHED needs a CLP           -> transformers / CTV / LEA (no CLP) can't.
// Breaker: everything except the measurement (C.M.) cells has one.
export const hasBreaker = (id: string): boolean => {
    const c = SRS_CELLS.find((x) => x.id === id);
    return !!c && !c.isMeasurement;
};
// CLP (earthing knife): the OE/generator bays and the measurement cells.
const CLP_BAY_IDS = new Set(["OE4", "LPGS2", "OE2", "GENERATOR 1", "GENERATOR 2", "OE3", "OE1"]);
export const hasClp = (id: string): boolean => {
    const c = SRS_CELLS.find((x) => x.id === id);
    return !!c && (!!c.isMeasurement || CLP_BAY_IDS.has(id));
};

// What switch a scenario's target state fundamentally needs (null = none/any).
export function scenarioRequires(scenarioId: string): "breaker" | "clp" | null {
    const def = SCENARIOS[scenarioId];
    if (!def || def.targetState === null) return null; // free
    if (def.startState === "EARTHED") return "clp";     // "revenire" out of earthed
    if (def.targetState === "DISCONNECTED") return "breaker";
    if (def.targetState === "EARTHED") return "clp";
    return null; // visibly-separated needs only separators, which every cell has
}

// Scenarios a single measurement cell (C.M.) can run: it has no breaker (so no
// "deconectat") and is never earthed on its own — only separated. It can be
// brought to visibly-separated / cold / cold-reserve (from normal) and returned
// to normal (from cold-reserve).
const CM_SCENARIOS = new Set(["free", "closing-cell", "ssv", "cold-condition", "cold-reserve"]);

// Is a scenario applicable to the chosen scope? For a single cell, the cell must
// have the required switch. Line / semistation / whole-station scopes always
// contain a measurement cell (CLP) and breakered bays, so they stay valid.
export function isScenarioValidForScope(scenarioId: string, selection: string): boolean {
    if (isSingleCellScope(selection)) {
        const cell = SRS_CELLS.find((c) => c.id === selection);
        if (cell?.isMeasurement) return CM_SCENARIOS.has(scenarioId);
    }
    const req = scenarioRequires(scenarioId);
    if (!req) return true;
    if (!isSingleCellScope(selection)) return true;
    return req === "breaker" ? hasBreaker(selection) : hasClp(selection);
}

// The operative-state NAME each scenario represents (several scenarios collapse
// to the same StateClass but keep distinct names, e.g. "rezerva rece" vs "stare
// rece" are both VISIBLY_SEPARATED). Used for the transition-card chips.
export const SCENARIO_STATE_LABEL: Record<string, string> = {
    free: "",
    "closing-cell": "Schemă normală (în funcțiune)",
    disconnected: "Deconectat",
    "warm-condition": "Stare caldă",
    "warm-reserve": "Rezervă caldă",
    ssv: "Separat vizibil",
    "cold-condition": "Stare rece",
    "cold-reserve": "Rezervă rece",
    slp: "Legat la pământ",
};

// Name of the state a scenario STARTS from, for the "Din starea" chip. Most start
// from normal operation; "revenire" starts from the out-of-service extreme
// (rezerva rece for a single measurement cell, legat la pamant otherwise).
export function scenarioStartLabel(scenarioId: string, selection: string): string {
    const def = SCENARIOS[scenarioId];
    if (!def || def.targetState === null) return "";
    if (def.startState === "EARTHED") {
        const cell = SRS_CELLS.find((c) => c.id === selection);
        if (isSingleCellScope(selection) && cell?.isMeasurement) return "Rezervă rece";
        return "Legat la pământ";
    }
    return "Schemă normală (în funcțiune)";
}

// Whether the chosen scope contains a generator bay (Generator 1 / Generator 2).
const GENERATOR_IDS = new Set(["GENERATOR 1", "GENERATOR 2"]);
export function scopeHasGenerator(selection: string): boolean {
    return resolveCells(selection).some((id) => GENERATOR_IDS.has(id));
}

// Representative start state for the transition preview, given the chosen scope.
// "Revenire" normally starts from earthed, but a single measurement cell starts
// from "rezerva rece" (visibly separated) instead.
export function scenarioStartState(scenarioId: string, selection: string): StateClass | null {
    const def = SCENARIOS[scenarioId];
    if (!def || def.targetState === null) return null;
    if (def.startState === "EARTHED") {
        const cell = SRS_CELLS.find((c) => c.id === selection);
        if (isSingleCellScope(selection) && cell?.isMeasurement) return "VISIBLY_SEPARATED";
        return "EARTHED";
    }
    return def.startState;
}

// For a scope reaching EARTHED, only the measurement cell(s) get the CLP closed
// (they earth the busbar); the rest of the scope is left visibly separated.
// A single-cell scope earths that cell itself (its own CLP).
// For a scope in NORMAL operation ("functiune"), a cell that is normally NOT in
// service keeps its own normal state — the C.T.V couplings stay visibly separated
// (they only close when a bar is taken out), and so does the Donalam reserve line.
// A single-cell scope is explicit: the trainer picked that cell, so it moves.
export function inScopeTarget(cellId: string, selection: string, scopeTarget: StateClass): StateClass {
    if (isSingleCellScope(selection)) return scopeTarget;
    if (scopeTarget === "NORMAL") return SRS_CELLS.find((c) => c.id === cellId)?.normalState || "NORMAL";
    if (scopeTarget !== "EARTHED") return scopeTarget;
    return SRS_CELLS.find((c) => c.id === cellId)?.isMeasurement ? "EARTHED" : "VISIBLY_SEPARATED";
}

// Full per-cell start/target state for the whole station given a scenario + scope.
// The whole station is in NORMAL operation; only the selected scope moves.
// "Revenire" scenarios (startState EARTHED) begin with the scope already earthed
// and return it to normal.
export function stationConfig(scenarioId: string, selection: string): {
    start: Record<string, StateClass>;
    target: Record<string, StateClass> | null;
} {
    const def = SCENARIOS[scenarioId];
    const start: Record<string, StateClass> = {};
    const target: Record<string, StateClass> = {};
    // Baseline = each cell's own normal operating state (most NORMAL; the Donalam
    // reserve is disconnected, the C.T.V cells visibly separated, etc.).
    SRS_CELLS.forEach((c) => { const n = c.normalState || "NORMAL"; start[c.id] = n; target[c.id] = n; });

    if (!def || def.targetState === null) return { start, target: null }; // free roam

    const revenire = def.startState === "EARTHED"; // "reach normal" starts from out-of-service
    const scope = resolveCells(selection);
    const selCell = SRS_CELLS.find((c) => c.id === selection);
    // A single measurement cell is never earthed on its own — its out-of-service
    // extreme is "rezerva rece" (visibly separated), so revenire starts from there.
    const singleCM = isSingleCellScope(selection) && !!selCell?.isMeasurement;
    // Earthing a whole semistation: the opposite transversal coupling (CTV) must
    // be brought into service (normal) to keep the other half fed; the longitudinal
    // couplings are part of the semistation scope, so they move with it.
    const OPPOSITE_CTV: Record<string, string> = { A: "C.T.V - B", B: "C.T.V - A" };
    if (revenire) {
        const from: StateClass = singleCM ? "VISIBLY_SEPARATED" : "EARTHED";
        scope.forEach((id) => { start[id] = inScopeTarget(id, selection, from); });
        if (selection === "A" || selection === "B") start[OPPOSITE_CTV[selection]] = "NORMAL";
        // target stays at each cell's normal state
    } else {
        scope.forEach((id) => { target[id] = inScopeTarget(id, selection, def.targetState as StateClass); });
        if (def.targetState === "EARTHED" && (selection === "A" || selection === "B"))
            target[OPPOSITE_CTV[selection]] = "NORMAL";
        // start stays at each cell's normal state
    }
    return { start, target };
}

// ---- Operative states (initial / final state pickers) ------------------------
// The trainer now picks BOTH the initial state and the final state. Each named
// operative state maps to a switch-position class (several names share a class).
export interface StateOption { id: string; label: string; stateClass: StateClass; }
export const STATE_OPTIONS: StateOption[] = [
    { id: "functiune",     label: "Funcțiune",             stateClass: "NORMAL" },
    { id: "deconectat",    label: "Stare deconectată",     stateClass: "DISCONNECTED" },
    { id: "calda",         label: "Stare caldă",           stateClass: "DISCONNECTED" },
    { id: "rezerva-calda", label: "Rezervă caldă",         stateClass: "DISCONNECTED" },
    { id: "ssv",           label: "Stare separat vizibil", stateClass: "VISIBLY_SEPARATED" },
    { id: "rece",          label: "Stare rece",            stateClass: "VISIBLY_SEPARATED" },
    { id: "rezerva-rece",  label: "Rezervă rece",          stateClass: "VISIBLY_SEPARATED" },
    { id: "slp",           label: "Stare legată la pământ", stateClass: "EARTHED" },
];
export const stateClassOf = (id: string): StateClass => STATE_OPTIONS.find((s) => s.id === id)?.stateClass || "NORMAL";
export const stateLabelOf = (id: string): string => STATE_OPTIONS.find((s) => s.id === id)?.label || id;

// ---- the operative-state ladder ---------------------------------------------
// An equipment is never taken straight from one state to a distant one: it is
// brought "in stare deconectata" before "separat vizibil", and only from there
// "legat la pamant" (regulament.txt §1889 / §1896). Every naming family keeps its
// own words along the way, so a cell asked for "rezerva rece" passes through
// "rezerva calda", and one asked for "stare rece" through "stare calda".
type StateFamily = "standard" | "reserve" | "condition";

// Index = how far out of service the state is (0 normal ... 3 earthed).
const LADDERS: Record<StateFamily, string[]> = {
    standard:  ["functiune", "deconectat",    "ssv",           "slp"],
    reserve:   ["functiune", "rezerva-calda", "rezerva-rece",  "slp"],
    condition: ["functiune", "calda",         "rece",          "slp"],
};
const FAMILY_OF: Record<string, StateFamily> = {
    deconectat: "standard", ssv: "standard",
    "rezerva-calda": "reserve", "rezerva-rece": "reserve",
    calda: "condition", rece: "condition",
};
const STATE_DEPTH: Record<StateClass, number> = {
    NORMAL: 0, DISCONNECTED: 1, VISIBLY_SEPARATED: 2, EARTHED: 3, ALL_OPEN: 3,
};
export const stateDepthOf = (id: string): number => STATE_DEPTH[stateClassOf(id)];

/**
 * Every operative state the exercise passes through, from `initialId` to
 * `finalId` inclusive. "Functiune" -> "separat vizibil" yields
 * [functiune, deconectat, ssv]. The intermediate states are named after the
 * family of whichever end of the journey has one.
 */
export function statePath(initialId: string, finalId: string): string[] {
    const family = FAMILY_OF[finalId] || FAMILY_OF[initialId] || "standard";
    const ladder = LADDERS[family];
    const di = stateDepthOf(initialId), df = stateDepthOf(finalId);
    if (di === df) return [initialId, finalId];
    const step = di < df ? 1 : -1;
    const out = [initialId];
    for (let d = di + step; d !== df; d += step) out.push(ladder[d]);
    out.push(finalId);
    return out;
}

/**
 * The full journey of a learning exercise: down to the requested state, then
 * back up the same ladder to the state it started from.
 * [functiune, deconectat, ssv, deconectat, functiune]
 */
export function stateJourney(initialId: string, finalId: string): string[] {
    const down = statePath(initialId, finalId);
    return [...down, ...down.slice(0, -1).reverse()];
}

// Can the scope actually reach this operative state? (Same equipment rules as
// scenarios: DISCONNECTED needs a breaker; EARTHED needs a CLP; a measurement
// cell has neither on its own, so only NORMAL / visibly-separated.)
export function isStateValidForScope(stateId: string, selection: string): boolean {
    const st = STATE_OPTIONS.find((s) => s.id === stateId);
    if (!st) return false;
    if (!isSingleCellScope(selection)) return true;
    const cell = SRS_CELLS.find((c) => c.id === selection);
    if (cell?.isMeasurement) return st.stateClass === "NORMAL" || st.stateClass === "VISIBLY_SEPARATED";
    if (st.stateClass === "DISCONNECTED") return hasBreaker(selection);
    if (st.stateClass === "EARTHED") return hasClp(selection);
    return true;
}

// Whole-station start/target config from an explicit initial + final state.
// Baseline is each cell's normal operating state; only the selected scope moves.
export function buildConfig(initialId: string, finalId: string, selection: string): {
    start: Record<string, StateClass>;
    target: Record<string, StateClass>;
} {
    const startClass = stateClassOf(initialId);
    const targetClass = stateClassOf(finalId);
    const start: Record<string, StateClass> = {};
    const target: Record<string, StateClass> = {};
    SRS_CELLS.forEach((c) => { const n = c.normalState || "NORMAL"; start[c.id] = n; target[c.id] = n; });

    const scope = resolveCells(selection);
    const OPPOSITE_CTV: Record<string, string> = { A: "C.T.V - B", B: "C.T.V - A" };
    scope.forEach((id) => {
        start[id] = inScopeTarget(id, selection, startClass);
        target[id] = inScopeTarget(id, selection, targetClass);
    });
    if (selection === "A" || selection === "B") {
        if (startClass === "EARTHED") start[OPPOSITE_CTV[selection]] = "NORMAL";
        if (targetClass === "EARTHED") target[OPPOSITE_CTV[selection]] = "NORMAL";
    }
    return { start, target };
}


// ---- Per-cell switch expectations -------------------------------------------
// Pure helpers shared by the scheme modules: which position each switch of a
// cell must sit in for a given state rule. Kept here (rather than next to the
// layout) so they carry no dependency on the drawing.

// Busbar a cell sits on in normal operation -> "I" | "II" (from its normalBar).
export const primaryBar = (id: string): "I" | "II" =>
    (SRS_CELLS.find((c) => c.id === id)?.normalBar || "I").startsWith("II") ? "II" : "I";

// Transversal couplings bridge both bars: when "connected", ALL bar seps close.
export const isCoupler = (id: string): boolean => !!SRS_CELLS.find((c) => c.id === id)?.isCoupler;

// Should this bar separator be closed under `rule`? On a "closed" bar-sep state
// only the cell's primary bar is closed (a bay sits on one bar); a coupler
// closes both.
export const wantBarSepClosed = (id: string, bar: "I" | "II", rule: StateRule): boolean =>
    rule.barSep === "closed" && (isCoupler(id) || bar === primaryBar(id));

// A generator runs only in normal operation (energized + breaker closed).
export const wantGenOn = (rule: StateRule): boolean => rule.energize && rule.breaker === "closed";

// Severity buckets for logged interlock warnings. Kept in sync with the backend
// scoring weights.
export type Severity = "critical" | "major" | "minor" | "info";

export const SEVERITY_WEIGHT: Record<Severity, number> = {
    critical: 40,
    major: 15,
    minor: 5,
    info: 0,
};
